import { CSSProperties, FC, useEffect, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import useMediaQuery from "@mui/material/useMediaQuery";
import { negativeState, positiveState, regionState, themeState } from "@/lib/store";
import ContentLayout from "@/layouts/content/ContentLayout";
import { getPulseSignals } from "@/controllers/pulse";
import { loadMIContent } from "@/controllers/market-intelligence";
import { MIContent } from "@/models/market-intelligence";
import useModalLink from "src/hooks/useModalLink";
import { PulseCategory } from "@/models/pulse";
import styles from "./MarketsPulse.module.scss";
import DetailDialog, { DetailContent } from "./DetailDialog";
import IndexPanel from "./IndexPanel";
import { articleDetail } from "./details";
import { CategoryPanel, EarningsPanel, MacroPanel, OutlierTab, OutlierTabs, ResearchPanel, ShortMoversPanel, TopSignals, useOpenItem } from "./Panels";
import PulseTape from "./PulseTape";
import { usePulseData } from "./usePulseData";
import { MARKETS, marketClock, sessionMode } from "./utils";

/** The data panels whose order follows the day: most unusual first. */
const RANKED: PulseCategory[] = ["shorts", "insiders", "gov", "options"];

/**
 * The front page: what is unusual right now across ORTEX's datasets.
 *
 * Reading order: the market (indexes, breadth, movers), then the biggest signals
 * across every dataset, then research. Below, one panel per dataset, ordered by how
 * unusual its top item is today, so the eye lands on the most interesting first.
 * Panels with nothing to say render nothing and the row closes up.
 */
const MarketsPulse: FC = () => {
    const theme = useRecoilValue(themeState);
    const positive = useRecoilValue(positiveState);
    const negative = useRecoilValue(negativeState);
    const region = useRecoilValue(regionState);
    const universeId = region === 10 ? 10 : 7;
    const [session, setSession] = useState(() => sessionMode());
    const [detail, setDetail] = useState<DetailContent | null>(null);
    const [now, setNow] = useState(() => new Date());

    useEffect(() => {
        const timer = setInterval(() => {
            setSession(sessionMode());
            setNow(new Date());
        }, 30_000);
        return () => clearInterval(timer);
    }, []);

    // One item per dataset: the order of the panels below.
    const { data: tops } = usePulseData((signal) => getPulseSignals({ limit: 8, perCategory: 1, universeId }, signal), [universeId], { refreshMs: 5 * 60_000 });
    const order = useMemo(() => {
        const interest: Record<string, number> = {};
        (tops?.rows || []).forEach((item) => (interest[item.category] = item.interest ?? item.score));
        const ranked = [...RANKED].sort((a, b) => (interest[b] ?? -1) - (interest[a] ?? -1));
        return Object.fromEntries(ranked.map((category, i) => [category, i + 1])) as Record<PulseCategory, number>;
    }, [tops]);

    // Which of alt data and macro is more unusual today: the tab that opens first.
    const outlierFirst: OutlierTab = useMemo(() => {
        const rows = tops?.rows || [];
        const score = (category: PulseCategory) => {
            const item = rows.find((row) => row.category === category);
            return item ? item.interest ?? item.score : -1;
        };
        return score("macro") > score("alt") ? "macro" : "alt";
    }, [tops]);

    // Desktop layouts fit one screen; below 1700px earnings, alt data and macro share one
    // tabbed column, above it earnings gets its own and alt data / macro share the last.
    const fit = useMediaQuery("(min-width:1280px) and (min-height:700px)");
    const wide = useMediaQuery("(min-width:1700px) and (min-height:700px)");

    const openItem = useOpenItem(setDetail);
    // Research opens by its link too: /?show=article&id=<content id>.
    const article = useModalLink<MIContent>("article", (id, signal) => loadMIContent(id, signal));
    const shown = article.item ? articleDetail(article.item) : detail;
    const closeDetail = () => {
        setDetail(null);
        article.close();
    };
    // The reader's own up/down colours (Settings), not fixed green and red.
    const colours = { "--mp-up": positive, "--mp-down": negative } as CSSProperties;

    return (
        <ContentLayout title="Market Pulse" help={{ moduleId: "market_overview", modulePage: "V2_market_overview" }}>
            <>
                <div className={`${styles.MarketsPulse} ${theme === "light" ? styles.light : ""}`} style={positive && negative ? colours : undefined}>
                    <div className={styles.sessionBar}>
                        <span className={styles.live}>
                            <i />
                            LIVE
                        </span>
                        <span className={styles.sessionLabel}>
                            {session.label} · {universeId === 10 ? "Europe" : "US & Canada"}
                        </span>
                        <span className={styles.spacer} />
                        <div className={styles.clocks} aria-label="Market hours">
                            {MARKETS.map((market) => {
                                const clock = marketClock(market, now);
                                return (
                                    <span key={market.name} className={`${styles.clock} ${clock.open ? styles.clockOpen : ""}`} title={clock.open ? `${market.name} is open` : `${market.name} is closed`}>
                                        <i />
                                        {market.name}
                                        <b className={styles.num}>{clock.time}</b>
                                    </span>
                                );
                            })}
                        </div>
                    </div>

                    <PulseTape universeId={universeId} onOpen={openItem} />

                    <div className={styles.board}>
                        <div className={styles.rowA}>
                            <IndexPanel mode={session.mode} universeId={universeId} />
                            <TopSignals universeId={universeId} onOpen={setDetail} />
                            <ResearchPanel onOpen={(content) => article.open(content.id, content)} />
                        </div>

                        <div className={styles.rowB}>
                            <ShortMoversPanel universeId={universeId} order={order.shorts} onOpen={setDetail} />
                            <CategoryPanel
                                category="insiders"
                                title="Insider buying"
                                info="Open-market buying by company insiders in the last 7 days, scored on size, seniority and how many insiders are buying together."
                                universeId={universeId}
                                order={order.insiders}
                                onOpen={setDetail}
                            />
                            {universeId === 7 && (
                                <CategoryPanel
                                    category="gov"
                                    title="US gov trades"
                                    info="Trades by members of Congress, the executive branch and the judiciary, disclosed in the last few days."
                                    universeId={universeId}
                                    order={order.gov}
                                    onOpen={setDetail}
                                />
                            )}
                            <CategoryPanel category="options" title="Options flow" info="Names with the most unusual option premium today, and whether it leans to calls or puts." universeId={universeId} order={order.options} onOpen={setDetail} />
                            {!fit && (
                                <>
                                    <EarningsPanel universeId={universeId} onOpen={setDetail} />
                                    <CategoryPanel category="alt" title="Alt data outliers" info="Alternative data that is new and unusual: readings at least 2σ from their own history, and fresh records, streaks and year-on-year moves. At most three rows per type of data." universeId={universeId} order={15} onOpen={setDetail} />
                                    <MacroPanel onOpen={setDetail} />
                                </>
                            )}
                            {fit && !wide && <OutlierTabs tabs={["earnings", "alt", "macro"]} universeId={universeId} onOpen={setDetail} />}
                            {wide && (
                                <>
                                    <EarningsPanel universeId={universeId} onOpen={setDetail} />
                                    <OutlierTabs tabs={["alt", "macro"]} initial={outlierFirst} universeId={universeId} onOpen={setDetail} />
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <DetailDialog content={shown} shareTitle={article.linked ? article.item?.headline : undefined} onClose={closeDetail} onLeave={() => setDetail(null)} />
            </>
        </ContentLayout>
    );
};

export default MarketsPulse;
